import { Link, useLocation } from 'react-router-dom';
import PageLayout from '../../components/base/PageLayout';
import NotFound from '../NotFound';
import articles from '../../data/articles.json';

/** 空行区切りの本文を段落に分けて描画する（区切りが無ければ従来どおり1段落）。 */
function Paras({ text, className }: { text: string; className?: string }) {
  const parts = text.split(/\n{2,}/);
  return (
    <>
      {parts.map((part, index) => (
        <p key={index} className={[className, index > 0 ? 'mt-5' : undefined].filter(Boolean).join(' ')}>
          {part}
        </p>
      ))}
    </>
  );
}

interface ArticleHero {
  src: string;
  alt: string;
}

interface ArticleImage {
  src: string;
  alt: string;
}

interface ArticleSection {
  heading: string;
  answer?: string;
  paragraphs: string[];
  image?: ArticleImage;
  subheads?: Record<string, string>;
  points?: { t: string; b: string }[];
}

interface ArticleFaq {
  q: string;
  a: string;
}

interface ArticleRelated {
  label: string;
  href: string;
}

interface ArticleChild {
  label: string;
  summary: string;
  href: string;
}

interface ArticleCta {
  heading: string;
  text: string;
  label: string;
  href: string;
}

interface ArticleEn {
  h1: string;
  kicker: string;
  lead: string;
  heroAlt?: string;
  sections: { heading: string; answer?: string; paragraphs: string[]; imageAlt?: string; subheads?: Record<string, string>; points?: { t: string; b: string }[] }[];
  faq?: { q: string; a: string }[];
  related?: { label: string; href: string }[];
  children?: { label: string; summary: string; href: string }[];
  cta?: { heading: string; text: string; label: string; href: string };
  category: string;
}

interface Article {
  route: string;
  category: string;
  h1: string;
  kicker: string;
  hero?: ArticleHero;
  lead: string;
  sections: ArticleSection[];
  faq?: ArticleFaq[];
  related?: ArticleRelated[];
  children?: ArticleChild[];
  cta?: ArticleCta;
  en?: ArticleEn;
}

function prefixEnHref(href: string): string {
  if (href.startsWith('/en/') || href === '/en') return href;
  if (href.startsWith('/')) return '/en' + href;
  return href;
}

function normalizePathname(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, '');
  return normalized === '' ? '/' : normalized;
}

export default function ArticlePage() {
  const { pathname } = useLocation();
  const normalized = normalizePathname(pathname);
  const isEn = normalized === '/en' || normalized.startsWith('/en/');
  const route = isEn ? (normalized.replace(/^\/en/, '') || '/') : normalized;
  const article = (articles as Article[]).find((a) => a.route === route);

  if (!article) return <NotFound />;
  if (isEn && !article.en) return <NotFound />;

  const en = article.en;
  const h1 = isEn && en ? en.h1 : article.h1;
  const kicker = isEn && en ? en.kicker : article.kicker;
  const lead = isEn && en ? en.lead : article.lead;
  const category = isEn && en ? en.category : article.category;
  const heroAlt = isEn && en ? en.heroAlt : article.hero?.alt;
  const sections = isEn && en ? en.sections : article.sections;
  const faq = isEn && en ? en.faq : article.faq;
  const related = isEn && en ? en.related : article.related;
  const children = isEn && en ? en.children : article.children;
  const cta = isEn && en ? en.cta : article.cta;

  const breadcrumbItems =
    article.route === '/butler'
      ? [{ name: h1 }]
      : [{ name: category }, { name: h1 }];

  return (
    <PageLayout
      breadcrumbItems={breadcrumbItems}
      hero={
        <section className="relative py-16 md:py-32 min-h-[420px] md:min-h-[560px] bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
          {article.hero && (
            <img
              src={article.hero.src}
              alt={heroAlt ?? ''}
              className="absolute inset-0 h-full w-full object-cover opacity-30"
            />
          )}
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 md:px-8 text-center">
            <p className="mb-4 text-sm tracking-[0.2em] text-amber-400">{kicker}</p>
            <h1
              className="font-serif text-3xl md:text-5xl font-light text-white tracking-wide"
              style={{ fontFamily: '"Noto Serif JP", serif' }}
            >
              {h1}
            </h1>
          </div>
        </section>
      }
    >
      <article className="bg-white" lang={isEn ? 'en' : 'ja'}>
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 pt-10 pb-16">
          <div className="mb-14">
            <Paras text={lead} className="text-lg leading-9 text-gray-700" />
          </div>

          {children && children.length > 0 && (
            <section className="mb-14">
              <h2 className="mb-6 border-l-4 border-amber-600 pl-4 font-serif text-2xl text-gray-900 md:text-3xl">
                {isEn ? 'Articles in This Category' : 'このカテゴリの記事'}
              </h2>
              <div className="grid gap-5 sm:grid-cols-2">
                {children.map((item) => (
                  <Link
                    key={item.href}
                    to={isEn ? prefixEnHref(item.href) : item.href}
                    className="block rounded border border-gray-200 p-5 transition-colors hover:border-amber-600 hover:bg-amber-50"
                  >
                    <span className="font-serif text-lg text-gray-900">{item.label}</span>
                    <p className="mt-2 text-sm leading-7 text-gray-600">{item.summary}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {sections.map((section, index) => {
            const imageSrc = isEn
              ? article.sections[index]?.image?.src
              : 'image' in section
                ? section.image?.src
                : undefined;
            const imageAlt = isEn
              ? ('imageAlt' in section ? section.imageAlt : undefined)
              : 'image' in section
                ? section.image?.alt
                : undefined;

            return (
              <section key={section.heading} className="mb-14">
                <h2 className="mb-6 border-l-4 border-amber-600 pl-4 font-serif text-2xl text-gray-900 md:text-3xl">
                  {section.heading}
                </h2>
                {section.answer && (
                  <div className="mb-6 border-l-2 border-amber-500 bg-amber-50 p-5 leading-8 text-gray-800">
                    <Paras text={section.answer} />
                  </div>
                )}
                {section.paragraphs.map((p, i) => (
                  <div key={i}>
                    {section.subheads?.[String(i)] && (
                      <h3 className="mb-3 mt-8 font-serif text-lg text-gray-900 md:text-xl">
                        <span className="mr-2 text-amber-600" aria-hidden="true">—</span>
                        {section.subheads[String(i)]}
                      </h3>
                    )}
                    <p className="mb-5 leading-8 text-gray-700">{p}</p>
                  </div>
                ))}
                {section.points && section.points.length > 0 && (
                  <div className="mb-6 grid gap-4 sm:grid-cols-3">
                    {section.points.map((pt) => (
                      <div key={pt.t} className="border border-amber-200 bg-amber-50/60 p-5">
                        <h3 className="font-serif text-base text-gray-900">{pt.t}</h3>
                        <div className="mt-2 text-sm leading-7 text-gray-700">{pt.b}</div>
                      </div>
                    ))}
                  </div>
                )}
                {imageSrc && (
                  <div className="aspect-[16/9] overflow-hidden rounded">
                    <img
                      src={imageSrc}
                      alt={imageAlt ?? ''}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              </section>
            );
          })}

          {faq && faq.length > 0 && (
            <section className="mb-14">
              <h2 className="mb-8 border-l-4 border-amber-600 pl-4 font-serif text-2xl text-gray-900 md:text-3xl">
                {isEn ? 'Frequently Asked Questions' : 'よくあるご質問'}
              </h2>
              <div className="space-y-8">
                {faq.map((item) => (
                  <div key={item.q}>
                    <h3 className="mb-3 font-serif text-xl text-gray-900">{item.q}</h3>
                    <Paras text={item.a} className="leading-8 text-gray-700" />
                  </div>
                ))}
              </div>
            </section>
          )}

          {related && related.length > 0 && (
            <section className="mb-14">
              <h2 className="mb-8 border-l-4 border-amber-600 pl-4 font-serif text-2xl text-gray-900 md:text-3xl">
                {isEn ? 'Related Pages' : '関連ページ'}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {related.map((item) => item.href.startsWith('https://') ? (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded border border-gray-200 bg-white p-5 transition-colors hover:border-amber-600 hover:bg-amber-50"
                  >
                    <span className="font-serif text-lg text-gray-900">{item.label}</span>
                  </a>
                ) : (
                  <Link
                    key={item.href}
                    to={isEn ? prefixEnHref(item.href) : item.href}
                    className="block rounded border border-gray-200 bg-white p-5 transition-colors hover:border-amber-600 hover:bg-amber-50"
                  >
                    <span className="font-serif text-lg text-gray-900">{item.label}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {cta && (
            <section className="rounded bg-amber-50 px-6 py-10 text-center md:px-10">
              <h2 className="mb-4 font-serif text-2xl text-gray-900 md:text-3xl">
                {cta.heading}
              </h2>
              <p className="mb-8 leading-8 text-gray-700">{cta.text}</p>
              <Link
                to={isEn ? prefixEnHref(cta.href) : cta.href}
                className="inline-block rounded bg-amber-700 px-8 py-3 font-medium text-white transition-colors hover:bg-amber-800"
              >
                {cta.label}
              </Link>
            </section>
          )}
        </div>
      </article>
    </PageLayout>
  );
}
